import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { VStack } from '@chakra-ui/react';

import { CompanyInvitationRole } from '__generated__/GQL';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import { logEvent } from 'features/Analytics/analytics';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import getIsMissingAccountHolder from '../../libs/getIsMissingAccountHolder';
import ManageBeneficiaryElement from './components/ManageBeneficiaryElement';
import beneficiaryLocales from './components/ManageBeneficiaryElement/locales';
import ManageCurrentUserElement from './components/ManageCurrentUserElement';
import MissingAccountHolderMessage from './components/MissingAccountHolderMessage';
import OwnershipOtherCompanyMessage from './components/OwnershipOtherCompanyMessage';
import { FormSchema } from './index';
import locales from './locales';
import { ManageBeneficiariesPageComponentProps } from './types';

const getConfirmationText = ({
  applicantType,
  applicationType,
  businessName,
}: {
  businessName: string;
  applicantType: ApplicantType.AccountHolder | ApplicantType.Mandatee;
  applicationType: ApplicationType.Standard | ApplicationType.CapitalDeposit;
}) => {
  const localeProps = {
    businessName,
  };

  if (businessName === '') {
    return locales.formatString(
      locales.confirmationTextUnknownBusinessName[applicantType][
        applicationType
      ],
      localeProps,
    ) as string;
  }

  if (businessName !== '') {
    return locales.formatString(
      locales.confirmationText[applicantType][applicationType],
      localeProps,
    ) as string;
  }

  throw new Error('Unexpected applicationType/businessName state');
};

const MAX_NUMBER_OF_SHAREHOLDERS = 20;

const ManageBeneficiariesPageComponent: FC<
  ManageBeneficiariesPageComponentProps
> = ({
  applicantType,
  applicationType,
  beneficiaries,
  businessName,
  canContinue,
  companyProfileId,
  currentUser,
  data,
  getIsAddedAmountSharesValid,
  hasAccountHolderInvitation,
  hasMissingEmails,
  hasMissingShareholders,
  header,
  includeInvitationRole,
  includeShares,
  isCreatingBeneficiary,
  isExistingCompany,
  isSaving,
  onAddAnother,
  onContinue,
  onDeleteSupposedBeneficiary,
  onEditingNewBeneficiary,
  onMoralBeneficiaryPress,
  sharesData,
  supposedBeneficiaries,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useFormContext<FormSchema>();

  const confirmationText = getConfirmationText({
    applicantType,
    applicationType,
    businessName,
  });

  let buttonLabel = locales.ctaConfirm;
  if (hasMissingEmails) {
    buttonLabel = locales.ctaMissingEmails;
  } else if (hasMissingShareholders) {
    buttonLabel = locales.ctaMissingShareholders;
  }

  const totalNumberOfShareholders = currentUser
    ? beneficiaries.length + 1
    : beneficiaries.length;

  return (
    <>
      {header}
      <VStack alignItems="flex-start" spacing="space-24">
        <VStack alignItems="flex-start" spacing="space-16" width="100%">
          {currentUser ? (
            <ManageCurrentUserElement
              applicationType={applicationType}
              companyProfileId={companyProfileId}
              getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
              includeShares={includeShares}
              isExistingCompany={isExistingCompany}
              sharesData={sharesData}
              user={currentUser}
            />
          ) : null}
          {errors?.hasAccountHolder?.message ? (
            <MissingAccountHolderMessage />
          ) : null}
          {beneficiaries.map((beneficiary) => (
            <ManageBeneficiaryElement
              beneficiary={beneficiary}
              companyProfileId={companyProfileId}
              disableAccountHolderSwitch={
                hasAccountHolderInvitation &&
                beneficiary?.invitation?.role !==
                  CompanyInvitationRole.AccountHolder
              }
              getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
              includeInvitationRole={includeInvitationRole}
              includeShares={includeShares}
              isExistingCompany={isExistingCompany}
              key={beneficiary.companyLegalBeneficiaryId}
              sharesData={sharesData}
            />
          ))}
          {supposedBeneficiaries.map((supposedBeneficiary) => (
            <ManageBeneficiaryElement
              beneficiary={supposedBeneficiary}
              companyProfileId={companyProfileId}
              disableAccountHolderSwitch={hasAccountHolderInvitation}
              getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
              includeInvitationRole={includeInvitationRole}
              includeShares={includeShares}
              isExistingCompany={isExistingCompany}
              key={supposedBeneficiary.supposedBeneficiaryId}
              onDelete={() => {
                onDeleteSupposedBeneficiary(
                  supposedBeneficiary.supposedBeneficiaryId,
                );
              }}
              sharesData={sharesData}
            />
          ))}
          {isCreatingBeneficiary ? (
            <ManageBeneficiaryElement
              companyProfileId={companyProfileId}
              disableAccountHolderSwitch={hasAccountHolderInvitation}
              getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
              includeInvitationRole={includeInvitationRole}
              includeShares={includeShares}
              isExistingCompany={isExistingCompany}
              onDoneEditing={onEditingNewBeneficiary}
              sharesData={sharesData}
            />
          ) : (
            <Button
              icon="plus"
              iconPosition="left"
              isDisabled={
                totalNumberOfShareholders >= MAX_NUMBER_OF_SHAREHOLDERS
              }
              onClick={onAddAnother}
              textAlign="left"
              variant="inline-primary"
            >
              {`${locales.addAnother[applicantType][applicationType]} (${totalNumberOfShareholders}/${MAX_NUMBER_OF_SHAREHOLDERS} max.)`}
            </Button>
          )}
        </VStack>
        <OwnershipOtherCompanyMessage
          applicantType={applicantType}
          onMoralBeneficiaryPress={onMoralBeneficiaryPress}
        />

        <Form.Field error={errors.isDoneAddingBeneficiaries?.message} label="">
          <Controller
            control={control}
            name="isDoneAddingBeneficiaries"
            render={({ field }) => (
              <Form.Checkbox
                id="all-beneficiaries-added"
                isChecked={field.value}
                onChange={(e) => {
                  logEvent({
                    name: 'Onboarding Beneficiary List Complete Toggled',
                    properties: {
                      value: e.target.checked,
                    },
                    usePrefix: false,
                  });
                  field.onChange(e);
                }}
              >
                <Form.Checkbox.Label variant="primary">
                  {confirmationText}
                </Form.Checkbox.Label>
              </Form.Checkbox>
            )}
          />
        </Form.Field>
        <Button
          alignSelf="center"
          isDisabled={!canContinue}
          isExpanded
          isLoading={isSaving}
          onClick={handleSubmit(() => {
            if (getIsMissingAccountHolder(data)) {
              setError('hasAccountHolder', {
                message: beneficiaryLocales.accountHolderError,
              });
              return;
            }

            onContinue();
          })}
        >
          {buttonLabel}
        </Button>
      </VStack>
    </>
  );
};

export default ManageBeneficiariesPageComponent;
